import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Linkedin, FileText, Calendar } from 'lucide-react';
import { ParticlesBackground } from '../ParticlesBackground';
import { Button } from '../ui/button';

const HEADSHOT_URL = "https://customer-assets.emergentagent.com/job_seth-ai-leadership/artifacts/m18g84sn_IMG_6440.jpg";

function AnimatedCounter({ target, label, suffix = '' }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [target]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="text-center"
    >
      <div className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold gradient-text">
        {count}{suffix}
      </div>
      <div className="text-sm sm:text-base text-muted-foreground mt-1">{label}</div>
    </motion.div>
  );
}

export function HeroSection() {
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Particles Background */}
      <ParticlesBackground />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/0 via-background/50 to-background z-10" />

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Profile Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="flex justify-center lg:justify-end order-1 lg:order-1"
          >
            <div className="relative">
              {/* Glow Effect */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-teal via-blue to-orange opacity-50 blur-3xl scale-110" />
              
              {/* Image Container */}
              <div className="relative w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 rounded-full overflow-hidden border-4 border-teal/30 animate-glow-pulse">
                <img
                  src={HEADSHOT_URL}
                  alt="Seth Cushing - AI Transformation Leader"
                  className="w-full h-full object-cover object-top"
                  data-testid="hero-headshot"
                />
              </div>
            </div>
          </motion.div>

          {/* Right - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center lg:text-left order-2 lg:order-2"
          >
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-teal font-mono text-sm uppercase tracking-widest mb-4"
            >
              CAP | PMP | AIPO
            </motion.p>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-heading font-bold mb-4">
              <span className="text-foreground">Seth</span>{' '}
              <span className="gradient-text">Cushing</span>
            </h1>

            <h2 className="text-xl sm:text-2xl lg:text-3xl font-heading font-semibold text-teal mb-2">
              AI Transformation Consultant
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground mb-6">
              AI Solution Architect
            </p>

            <p className="text-lg sm:text-xl text-foreground/90 mb-8 max-w-xl mx-auto lg:mx-0">
              Building Human + AI Organizations
            </p>

            <p className="text-base text-muted-foreground mb-10 max-w-xl mx-auto lg:mx-0">
              Seth Cushing is an enterprise technology leader focused on designing and 
              implementing AI-enabled organizations, platforms, and products. His work 
              spans Fortune 500 enterprises, government modernization programs, and 
              high-growth technology companies.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
              <Button
                data-testid="hero-cta-schedule"
                onClick={scrollToContact}
                size="lg"
                className="bg-teal hover:bg-teal-dark text-white px-8"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Schedule a Conversation
              </Button>
              <Button
                data-testid="hero-cta-linkedin"
                variant="outline"
                size="lg"
                className="border-teal/50 hover:bg-teal/10"
                asChild
              >
                <a href="https://www.linkedin.com/in/seth-cushing-cap-pmp-aipo-259ba1124/" target="_blank" rel="noopener noreferrer">
                  <Linkedin className="w-5 h-5 mr-2" />
                  LinkedIn
                </a>
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Animated Counters */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 lg:mt-24"
        >
          <div className="glass-light rounded-2xl p-8 lg:p-12">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              <AnimatedCounter target={18} label="Years in Technology" suffix="+" />
              <AnimatedCounter target={47} label="Enterprise Platforms" suffix="" />
              <AnimatedCounter target={12} label="AI Transformations Led" suffix="" />
              <AnimatedCounter target={223} label="Products Built" suffix="" />
            </div>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="text-muted-foreground"
          >
            <ChevronDown className="w-8 h-8" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
